import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';

import { Container, Loading, SimpleButton, SimpleTextField } from '@/src/ui';
import { apiRequest } from '@/src/services';
import QuestionsTable from './components/QuestionsTable';
import ViewQuestionsFilters from './components/ViewQuestionsFilters';
import DeleteQuestionModal from './components/DeleteQuestionModal';
import PreviewModal from './components/PreviewModal';
import { useAuth } from '@/src/app/context/useAuth';
import { type FilterState } from './models/filterDTOs';
import { type QuestionDTO } from './models/questionDTOs';

const ViewQuestionsPage: React.FC = () => {
    document.title = 'Questions';

    const { user } = useAuth();
    const userId = user?.id || 'nouser';
    const FILTER_KEY = `viewquestions-filters-${userId}`;

    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');

    const [searchString, setSearchString] = useState('');
    const [data, setData] = useState<Array<QuestionDTO>>([]);
    const [filterState, setFilterState] = useState(() => {
        try {
            const saved = localStorage.getItem(FILTER_KEY);
            if (saved) return JSON.parse(saved);
        } catch {
            // intentionally empty
        }
        return {
            gradeLevelMasterId: 0,
            standardId: 0,
            questionTypeMasterId: 0,
            isPracticeQuestion: false,
        };
    });

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [selectedQuestion, setSelectedQuestion] = useState<QuestionDTO | null>(null);

    const navigate = useNavigate();

    const refreshTableData = useCallback(
        async ({
            gradeLevelMasterId,
            standardId,
            questionTypeMasterId,
            isPracticeQuestion,
        }: FilterState = filterState) => {
            setError('');
            setLoaded(false);

            let requestUrl = `assessments/questions`;
            const reqParams = [];
            if (gradeLevelMasterId !== 0) {
                reqParams.push(`gradeLevelMasterIds=${gradeLevelMasterId}`);
            }
            if (standardId !== 0) {
                reqParams.push(`standardIds=${standardId}`);
            }
            reqParams.push(`questionTypeMasterId=${questionTypeMasterId}`);
            reqParams.push(`isPracticeQuestion=${isPracticeQuestion}`);

            requestUrl += `?${reqParams.join('&')}`;

            try {
                const res = await apiRequest<QuestionDTO[]>('GET', requestUrl);
                setData(res.data);
            } catch {
                setError('Failed to fetch question data');
            } finally {
                setLoaded(true);
            }
        },
        [filterState]
    );

    const handleModalClose = useCallback(
        (shouldRefresh: boolean, isInTimeout: boolean) => {
            setShowDeleteModal(false);
            setShowPreviewModal(false);
            // Don't allow the modal to be reopened while it is in its post-submit timeout
            if (!isInTimeout) {
                setSelectedQuestion(null);
            }

            if (shouldRefresh) {
                refreshTableData();
            }
        },
        [refreshTableData]
    );

    useEffect(() => {
        (async function init() {
            await refreshTableData();
        })();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!userId) return;
        try {
            localStorage.setItem(FILTER_KEY, JSON.stringify(filterState));
        } catch {
            // intentionally empty
        }
    }, [filterState, userId, FILTER_KEY]);

    return (
        <Container>
            <h1 className="page-header">Questions</h1>
            <br />

            <ViewQuestionsFilters
                refreshTableData={refreshTableData}
                filterState={filterState}
                setFilterState={setFilterState}
            />

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                }}
            >
                <SimpleTextField
                    name="searchQuestions"
                    label="Search by title..."
                    variant="outlined"
                    value={searchString}
                    onChange={(e) => setSearchString(e.target.value)}
                    sx={{ minWidth: 170, maxWidth: 300, mr: 2 }}
                />

                <SimpleButton
                    onClick={() => navigate(`/questions/createEditQuestion?viewMode=create`)}
                    variant="contained"
                    color="primary"
                    className="whitespace-nowrap"
                >
                    Add Question
                </SimpleButton>
            </Box>

            {!loaded && <Loading message="Loading questions..." />}
            {error && <div className="text-red-500 text-center">{error}</div>}

            {loaded && (
                <QuestionsTable
                    data={data}
                    showDeleteModal={showDeleteModal}
                    setShowDeleteModal={setShowDeleteModal}
                    setShowPreviewModal={setShowPreviewModal}
                    setSelectedQuestion={setSelectedQuestion}
                    searchString={searchString}
                />
            )}

            {showPreviewModal && selectedQuestion?.id && (
                <PreviewModal
                    open={showPreviewModal}
                    resourceId={selectedQuestion?.id}
                    handleClose={handleModalClose}
                />
            )}

            {showDeleteModal && selectedQuestion?.id && (
                <DeleteQuestionModal
                    open={showDeleteModal}
                    resourceId={selectedQuestion?.id}
                    handleClose={handleModalClose}
                />
            )}
        </Container>
    );
};

export default ViewQuestionsPage;
